import React, {Fragment, useEffect, useRef, useState, useCallback} from "react";
import moment from 'moment'
import {useSelector, useDispatch} from "react-redux";
import SearchForm from "../layout/SearchForm";
import FilterByMonth from "../layout/FilterByMonth";
import ProductDataRow from "./ProductDataRow";
import {getProductsTransactions, downloadProductReport} from "../../actions/products_transaction";
import ProductReport from "./ProductReport";
import useDates from "../../utils/useDates";
import CustomPaginationWithManyPages from "../layout/CustomPaginationWithManyPages";
import { usePermission } from '../../hooks/usePermission';
import useActiveFirm from '../../hooks/useActiveFirm';
import {getParams} from "../../utils/hooks/getParams";

const ProductCalculationsNew = () => {
    const { userCan } = usePermission();
    const dispatch = useDispatch();
    const { firmId, firmName } = useActiveFirm();
    const {dates, updateDates} = useDates();
    const {
        products,
        loading,
        numberOfPages,
        current_page,
    } = useSelector(state => state.products_transaction);

    // Check base permission first
    if (!userCan('view product transactions')) {
        return <div>You don't have permission to view product transactions.</div>;
    }

    // Wrapper function to ensure firm_id is always included when calling getProductsTransactions
    const getProductsTransactionsWithFirm = useCallback((page, params = {}) => {
        const filters = { ...params };
        if (firmId) {
            filters.firm_id = firmId;
        }
        // Clean up undefined/null values
        Object.keys(filters).forEach(key => {
            if (filters[key] === undefined || filters[key] === null || filters[key] === '' || filters[key] === 'undefined' || filters[key] === 'null') {
                delete filters[key];
            }
        });
        return getProductsTransactions(page, filters);
    }, [firmId]);

    // Fetch data when firm changes
    useEffect(() => {
        if (firmId) {
            dispatch(getProductsTransactionsWithFirm(1, getParams()));
        }
    }, [dispatch, firmId, getProductsTransactionsWithFirm]);

    const scrollableContainer = useRef();
    const [isFirst, setIsFirst] = useState(true);

    useEffect(() => {
        if (products && products.length > 0 && products[0].hasOwnProperty('transactions')) {
            if (isFirst === true && scrollableContainer.current) {
                scrollableContainer.current.scrollLeft += scrollableContainer.current.scrollHeight * 30;
                setIsFirst(false);
            }
        }
    }, [products])

    const onMonthChange = (newYearMonth) => {
        updateDates(newYearMonth);
    }

    return (
        <div className={'px-4'}>
            <div className="row mt-4 mb-2 justify-content-between">
                <div className="col-md-12 mb-2">
                    <h5>
                        Mahsulot hisobotlari
                        {firmName && <span className="text-muted ms-2" style={{fontSize: '14px', fontWeight: 'normal'}}>— {firmName}</span>}
                    </h5>
                </div>
                <div className="col-md-6 me-auto">
                    <SearchForm getPageItems={getProductsTransactionsWithFirm}/>
                </div>
                <div className="col-md-3 align-self-end">
                    <FilterByMonth change={onMonthChange} getPageItems={getProductsTransactionsWithFirm}/>
                </div>
            </div>

            <div className="row mt-3">
                <div className={`loader-container ${loading ? 'loading' : ''} `}></div>

                <div ref={scrollableContainer} className={`tableFixHead ${loading ? 'data_loader' : ''}`}>
                    {products && products.length > 0 ? (
                        <table className={'table-hover custom-hover'}>
                            <thead>
                            <tr>
                                <th className="sticky-col" style={{width: "2em"}}>#</th>
                                <th className="sticky-col2">Mahsulot</th>
                                {dates?.map((item, index, items) => (
                                    items.length - 1 === index ? (
                                        <th key={`date${index}`} className="sticky-col"
                                            style={{textAlign: "center"}}
                                            colSpan="2">
                                            {item}
                                            {moment(new Date()).format('YYYY-MM-DD') === item ? '(bugun)' : ''}
                                        </th>
                                    ) : (
                                        <th key={`date${index}`} className="sticky-col"
                                            style={{textAlign: "center"}}
                                            colSpan="2">{item}
                                        </th>
                                    )
                                ))}
                                <th className="sticky-col" style={{textAlign: "center", right: 0}}
                                    colSpan="5">Oy bo'yicha umumiy hisob
                                </th>
                            </tr>
                            <tr>
                                <th className="sticky-col">-</th>
                                <th className="sticky-col2"></th>
                                {dates.map((subitem, index) => (
                                    <Fragment key={`th${index}`}>
                                        <th className="sticky-col"
                                            style={{textAlign: "center"}}>Chiqarilgan
                                        </th>
                                        <th className="sticky-col"
                                            style={{textAlign: "center"}}>Sotilgan
                                        </th>
                                    </Fragment>
                                ))}
                                <th className="sticky-col"
                                    style={{textAlign: "center", minWidth: "100px", right: "400px"}}>
                                    Ishlab chiqarilgan
                                </th>
                                <th className="sticky-col"
                                    style={{textAlign: "center", minWidth: "100px", right: "300px"}}>
                                    Sotilgan
                                </th>
                                <th className="sticky-col"
                                    style={{textAlign: "center", minWidth: "100px", right: "200px"}}>
                                    Umumiy chiqarilgan
                                </th>
                                <th className="sticky-col"
                                    style={{textAlign: "center", minWidth: "100px", right: "100px"}}>
                                    Umumiy sotilgan
                                </th>
                                <th className="sticky-col"
                                    style={{textAlign: "center", minWidth: "100px", right: "0px"}}>
                                    Qoldiq
                                </th>
                            </tr>
                            </thead>

                            <tbody>
                            {products?.map((item, index) => (
                                <ProductDataRow 
                                    item={item}
                                    key={index}
                                    index={index}
                                    dates={dates}
                                />
                            ))}
                            </tbody>
                        </table>
                    ) : ''}
                </div>
            </div>

            <div className="row mt-3">
                <CustomPaginationWithManyPages
                    numberOfPages={numberOfPages}
                    getPageItems={getProductsTransactionsWithFirm}
                    current_page={current_page}
                />
            </div>

            {userCan('generate product transactions report') && (
                <ProductReport downloadProductReport={downloadProductReport}/>
            )}
        </div>
    )
}

export default ProductCalculationsNew;
